/**
 * Creates comprehensive user traits for analytics identify() function
 * Based on the task requirements for consistent attribution across events
 * Works in both Ember and non-Ember environments
 */

/**
 * Helper function to safely access object properties
 * Works with both Ember objects (using .get()) and plain JavaScript objects
 */
function safeGet(obj, path, defaultValue = undefined) {
	if (!obj) return defaultValue;

	// If object has .get method (Ember object), use it
	if (typeof obj.get === 'function') {
		return obj.get(path) || defaultValue;
	}

	// For plain JavaScript objects, handle dot notation
	if (typeof path === 'string' && path.includes('.')) {
		const keys = path.split('.');
		let current = obj;
		for (const key of keys) {
			if (current && typeof current === 'object' && key in current) {
				current = current[key];
			} else {
				return defaultValue;
			}
		}
		return current || defaultValue;
	}

	// For simple property access
	return obj[path] || defaultValue;
}

/**
 * Maps subscription status to the required format
 */
function mapSubscriptionStatus(status) {
	return status;
}

/**
 * Maps plan period to the required format
 */
function mapPlanInterval(planPeriod) {
	switch (planPeriod?.toLowerCase()) {
		case 'month':
			return 'month';
		case 'year':
			return 'year';
		default:
			return undefined;
	}
}

/**
 * Determines plan ID based on subscription data
 */
function getPlanId(subscription) {
	if (!subscription) return 'unknown';

	// Try to get from nested subscriptionPlan object
	const subscriptionPlan = safeGet(subscription, 'subscriptionPlan');
	if (subscriptionPlan) {
		return subscriptionPlan.id || subscriptionPlan.planType || 'unknown';
	}

	// Fallback to direct properties
	return subscription.id || subscription.planType || 'unknown';
}

/**
 * Formats phone number to E.164 format if possible
 */
function formatPhoneNumber(phoneNumber, countryCode) {
	if (!phoneNumber) return undefined;

	// If already in E.164 format (starts with +), return as is
	if (phoneNumber.startsWith('+')) {
		return phoneNumber;
	}

	// If we have country code, try to format
	if (countryCode) {
		// Remove any non-digit characters
		const digits = phoneNumber.replace(/\D/g, '');
		return `+${countryCode}${digits}`;
	}

	// Return as is if we can't format properly
	return phoneNumber;
}

/**
 * Creates comprehensive traits for identify() function
 */
export function createComprehensiveTraits(user, userSubscription = null, lastLoginPlatform = 'web') {
	// Build comprehensive traits with only the required fields from the task
	const comprehensiveTraits = {
		// Identity & Contact
		user_id: safeGet(user, 'id', ''),
		email: safeGet(user, 'email', ''),
		phone: safeGet(user, 'phoneNumber') ? formatPhoneNumber(safeGet(user, 'phoneNumber'), safeGet(user, 'phoneNumberCountryDialCode')) : undefined,
		name: safeGet(user, 'firstName') && safeGet(user, 'lastName') ? `${safeGet(user, 'firstName')} ${safeGet(user, 'lastName')}` : undefined,
		first_name: safeGet(user, 'firstName'),
		last_name: safeGet(user, 'lastName'),
		customer_since: safeGet(user, 'registrationDate'),

		// Account / Subscription State
		plan: {
			id: getPlanId(userSubscription),
			interval: userSubscription ? mapPlanInterval(safeGet(userSubscription, 'subscriptionPlan.planPeriod')) : undefined,
			status: userSubscription ? mapSubscriptionStatus(safeGet(userSubscription, 'status')) : 'visitor',
			started_at: safeGet(userSubscription, 'subscribedAt'),
			canceled_at: safeGet(userSubscription, 'status') === 'cancelled' ? safeGet(userSubscription, 'periodEndAt') : undefined
		},
		currency_preference: safeGet(user, 'currency')?.toUpperCase(),

		// Locale / Profile Meta
		country: safeGet(user, 'country')?.toUpperCase(),
		timezone: safeGet(user, 'timezone'),
		locale: safeGet(user, 'locale'),

		// App / Device Last-Seen Pointers
		last_login_platform: lastLoginPlatform,
		last_login_at: new Date().toISOString()
	};

	return comprehensiveTraits;
}